import React from 'react'
import {SplitBookingOfferInfo} from 'components/Offer/types'

import {Box} from '@vio/atlas/helpers/Box'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {OfferLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {OfferSecondaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {OfferLogo} from '../OfferLogo'
import {shouldOfferBeBranded} from '../utils'

type ProviderLogoVariants = Exclude<
  OfferLayoutVariant,
  OfferSecondaryLayoutVariant
>

interface MainProps {
  hotelId: string
  variant: ProviderLogoVariants
  offer: SearchOffer
  isAppLockedDeal: boolean
  isSplitBooking?: false
  splitBookingOffers?: never[]
}

interface SplitBookingProps {
  hotelId: string
  variant: ProviderLogoVariants
  isSplitBooking: true
  isAppLockedDeal?: never
  splitBookingOffers: SplitBookingOfferInfo[]
  offer?: never
}

export const ProviderLogoComponent: React.FC<MainProps | SplitBookingProps> = ({
  hotelId,
  isSplitBooking = false,
  splitBookingOffers = [],
  offer,
  variant,
  isAppLockedDeal
}) => {
  const {brandCode} = useBrand()
  const {
    monetisation: {hasBrandedOffers},
    commerce: {bookOnBrand}
  } = useBrandCapabilities()
  const isBookOnBrand = bookOnBrand.enabled

  if (isSplitBooking) {
    const firstOffer = splitBookingOffers[0]?.offer
    const secondOffer = splitBookingOffers[1]?.offer

    const isSameProvider =
      firstOffer.intermediaryProvider === secondOffer.intermediaryProvider

    const isFirstOfferBranded = shouldOfferBeBranded({
      offerIntermediaryProviderCode: firstOffer?.intermediaryProvider,
      isBookOnBrand,
      hasBrandedOffers
    })

    const isSecondOfferBranded = shouldOfferBeBranded({
      offerIntermediaryProviderCode: secondOffer?.intermediaryProvider,
      isBookOnBrand,
      hasBrandedOffers
    })

    return (
      <Box
        data-id="Logo"
        display="flex"
        flexDirection="column"
        gap="s200"
        marginTop="s200"
        marginBottom={variant !== 'PrimaryDefault' ? 's200' : '0'}
      >
        <OfferLogo
          hotelId={hotelId}
          providerCodeOverride={
            (isAppLockedDeal || isFirstOfferBranded) && brandCode
          }
          offer={firstOffer}
          isSplitBooking
          isPrimaryOffer
          isAppLockedDeal={isAppLockedDeal}
        />
        {!isSameProvider && (
          <OfferLogo
            hotelId={hotelId}
            offer={secondOffer}
            isSplitBooking
            isPrimaryOffer
            providerCodeOverride={isSecondOfferBranded && brandCode}
          />
        )}
      </Box>
    )
  }

  const {intermediaryProvider} = offer || {}

  const isBrandedDeal = shouldOfferBeBranded({
    offerIntermediaryProviderCode: intermediaryProvider,
    isBookOnBrand,
    hasBrandedOffers
  })

  return (
    <Box
      data-id="Logo"
      display="flex"
      flexDirection="column"
      gap="s200"
      marginTop="s200"
      marginBottom={variant !== 'PrimaryDefault' ? 's200' : '0'}
    >
      <OfferLogo
        hotelId={hotelId}
        offer={offer}
        isPrimaryOffer
        providerCodeOverride={(isAppLockedDeal || isBrandedDeal) && brandCode}
        isAppLockedDeal={isAppLockedDeal}
      />
    </Box>
  )
}
